import { FieldDataType, FieldType } from 'module/api/field/types';
import {
  ChildResource,
  IntegrationField,
  RLProjectFieldMapping,
  SalesforceActions,
} from 'module/api/integrations/salesforce/types';
import {
  useSalesForceChildResourcesQuery,
  useSalesForceContactMandatoryFieldsQuery,
} from 'queries/salesforce';
import { useEffect, useMemo, useState } from 'react';
import { useAutomationContext } from 'components/organisms/integrations/salesforce/create/automation-context';
import { getFields } from 'components/organisms/integrations/salesforce/create/blocks/shared/block-utils';

const transformChildResourcesToFields = (
  childResources: ChildResource[],
  selectedContactObject?: {
    sourceResourceType: string;
    sourceRelationshipName: string;
  },
): IntegrationField[] => {
  const countsByType = (Array.isArray(childResources) ? childResources : []).reduce(
    (acc, r) => {
      acc[r.sourceResourceType] = (acc[r.sourceResourceType] ?? 0) + 1;

      return acc;
    },
    {} as Record<string, number>,
  );

  // Always create options array, even if empty
  const options =
    Array.isArray(childResources) && childResources.length > 0
      ? childResources.map((resource, index) => {
        const isDuplicatedType =
          (countsByType[resource.sourceResourceType] ?? 0) > 1;
        const pillText =
          isDuplicatedType && resource.sourceParentResourceFieldId
            ? resource.sourceParentResourceFieldId
            : undefined;

        return {
          // Keep label as the resource name; pill renders separately
          label: resource.sourceResourceType,
          valueStr: resource.sourceRelationshipName,
          value: index,
          active: selectedContactObject
            ? selectedContactObject.sourceResourceType ===
            resource.sourceResourceType &&
            selectedContactObject.sourceRelationshipName ===
            resource.sourceRelationshipName
            : true,
          valueMeta: {
            sourceResourceType: resource.sourceResourceType,
            sourceRelationshipName: resource.sourceRelationshipName,
            sourceParentResourceFieldId: resource.sourceParentResourceFieldId,
            pillText,
          },
        };
      })
      : [];

  // Always return the Contact object field, even if options are empty
  return [
    {
      integrationFieldId: 'contactObject',
      parentFieldName: 'Contact',
      fieldName: 'contactObject',
      fieldLabel: 'Contact object',
      fieldDataType: FieldDataType.Text,
      fieldType: FieldType.SingleSelect,
      isEnabled: true,
      private: false,
      customField: false,
      optionsAvailable: true,
      fieldSectionId: 0,
      objectType: 'COMPANY' as any,
      userGroupIds: [],
      isEditable: true,
      formulaFieldMeta: {} as any,
      metaData: {
        options,
      },
    },
  ];
};

interface UseAutomationContactOptions {
  integrationId: string;
  isContactSelected: boolean;
  isEditMode?: boolean;
}
const salesForceActionsType = SalesforceActions.Contact;

export const useAutomationContact = ({
  integrationId,
  isContactSelected,
  isEditMode = false,
}: UseAutomationContactOptions) => {
  const {
    automationDetails,
    setAutomationDetails,
    setIsContactSyncConfigured,
  } = useAutomationContext();

  const [actionDrawerVisible, setActionDrawerVisible] = useState(false);

  const { sourceResourceType, sourceRelationshipName } =
    automationDetails || {};

  const fieldsKey = getFields(salesForceActionsType);

  const { salesforceObject, salesForceType, rocketlaneFields } =
    automationDetails || {};

  const { value } = salesforceObject || {};
  const { companyObject } = rocketlaneFields || {};

  const isAccountCreation = salesForceType === SalesforceActions.Account;

  // ✅ For both Account and Project, use the main Salesforce object as parent
  const parentObject = value;

  // ✅ For Project: pass the companyObject placeholder (e.g., "${trigger.AccountId.Id}")
  // ✅ For Account: pass undefined (no param needed)
  const triggerObjectParentPlaceholder = isAccountCreation
    ? undefined
    : companyObject?.valueStr;

  const {
    data: childResourcesData,
    isLoading: isChildResourcesLoading,
    isFetching: isChildResourcesFetching,
  } = useSalesForceChildResourcesQuery(
    parseInt(integrationId || '0'),
    String(parentObject || ''),
    triggerObjectParentPlaceholder,
    {
      enabled: isContactSelected && !!parentObject && !!integrationId,
      refetchOnMount: false,
    },
  );

  // Validate contact field sync configuration
  // Returns list of missing/invalid mandatory fields
  const { data: missingMandatoryFields, isLoading: isContactFieldsLoading } =
    useSalesForceContactMandatoryFieldsQuery(
      parseInt(integrationId || '0'),
      String(sourceResourceType || ''),
      {
        enabled:
          isContactSelected &&
          !!integrationId &&
          !!childResourcesData &&
          !!sourceResourceType,
      },
    );

  // Check if sync is configured (empty array means all fields are mapped)
  const isSyncConfigured = missingMandatoryFields?.length === 0;

  // Update context when sync status changes
  useEffect(() => {
    setIsContactSyncConfigured(isSyncConfigured);
  }, [isSyncConfigured, setIsContactSyncConfigured]);

  // Hide fields in UI if sync is not configured
  // Only show the contact object selector, not the individual fields
  const contactFieldsForSelectedObject = useMemo(() => {
    // If sync is not configured, return empty array (fields will be hidden)
    if (!isSyncConfigured) {
      return [];
    }

    // If sync is configured, we don't need to show additional fields
    // The contactObject field is already shown from transformChildResourcesToFields
    return [];
  }, [isSyncConfigured]);

  useEffect(() => {
    if (
      isEditMode &&
      sourceResourceType &&
      sourceRelationshipName &&
      automationDetails?.actions?.[1]?.inputs
    ) {
      const { salesforceRelatedResources = [] } = childResourcesData || {};
      const matched = salesforceRelatedResources.find(
        (r) =>
          r.sourceResourceType === sourceResourceType &&
          r.sourceRelationshipName === sourceRelationshipName,
      );

      const countsByType = salesforceRelatedResources.reduce(
        (acc, r) => {
          acc[r.sourceResourceType] = (acc[r.sourceResourceType] ?? 0) + 1;

          return acc;
        },
        {} as Record<string, number>,
      );
      const isDuplicatedType =
        matched != null &&
        (countsByType[matched.sourceResourceType] ?? 0) > 1;

      setAutomationDetails({
        [fieldsKey]: {
          contactObject: {
            label: sourceResourceType,
            valueStr: sourceRelationshipName,
            valueMeta: {
              sourceResourceType: sourceResourceType,
              sourceRelationshipName: sourceRelationshipName,
              sourceParentResourceFieldId: matched?.sourceParentResourceFieldId,
              pillText:
                isDuplicatedType && matched?.sourceParentResourceFieldId
                  ? matched.sourceParentResourceFieldId
                  : undefined,
            },
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isEditMode,
    sourceResourceType,
    sourceRelationshipName,
    childResourcesData,
  ]);

  const allFields = useMemo(() => {
    const { salesforceRelatedResources = [] } = childResourcesData || {};

    const selectedContactObject =
      sourceResourceType && sourceRelationshipName
        ? {
          sourceResourceType,
          sourceRelationshipName,
        }
        : undefined;

    const transformedChildResources = transformChildResourcesToFields(
      salesforceRelatedResources,
      selectedContactObject,
    );

    const contactFields =
      sourceResourceType && sourceRelationshipName
        ? (contactFieldsForSelectedObject ?? [])
        : [];

    return [...transformedChildResources, ...contactFields];
  }, [
    contactFieldsForSelectedObject,
    childResourcesData,
    sourceResourceType,
    sourceRelationshipName,
  ]);

  const handleContactObjectChange = (
    contactObjectValue: {
      sourceResourceType: string;
      sourceRelationshipName: string;
    } | null,
  ) => {
    setAutomationDetails({
      sourceResourceType: contactObjectValue?.sourceResourceType,
      sourceRelationshipName: contactObjectValue?.sourceRelationshipName,
    });
  };

  const onSave = (updatedFields: RLProjectFieldMapping) => {
    const fields = Object.keys(updatedFields).reduce((acc, key) => {
      acc[key] =
        key !== 'teamMembers'
          ? updatedFields[key] != null
            ? {
              ...updatedFields[key],
              label:
                updatedFields[key]?.valueMeta?.fieldLabel ??
                updatedFields[key]?.label,
            }
            : undefined
          : updatedFields[key];

      return acc;
    }, {});

    const contactObject = updatedFields?.contactObject;
    const sourceResourceType = contactObject?.valueMeta?.sourceResourceType;
    const sourceRelationshipName =
      contactObject?.valueMeta?.sourceRelationshipName;

    // Always prefer fresh API data when available (fixes edit mode object changes)
    // Falls back to cached data during initial load before API responds
    let sourceParentResourceId =
      childResourcesData?.sourceParentResourceId ||
      (automationDetails?.salesforceObject?.value as string);

    const sourceParentResourceType =
      (childResourcesData?.sourceParentResourceType as string) ||
      (automationDetails?.salesforceObject?.value as string);

    // const sourceParentResourceType = isEditMode
    //   ? (automationDetails?.salesforceObject?.value as string)
    //   : (childResourcesData?.sourceParentResourceType as string);

    setAutomationDetails({
      [fieldsKey]: fields,
      sourceResourceType,
      sourceRelationshipName,
      sourceParentResourceId,
      sourceParentResourceType,
    });
  };

  return {
    allFields,
    isLoading: isChildResourcesLoading || isChildResourcesFetching,
    isContactFieldsLoading,
    actionDrawerVisible,
    setActionDrawerVisible,
    fieldsKey,
    salesForceActionsType,
    value,
    onSave,
    handleContactObjectChange,
    isSyncConfigured,
    missingMandatoryFields,
  };
};
